/**
 * A "impressão digital" do que o rascunho guardaria — para o `persist()` saber se algo MUDOU.
 *
 * LOTE 4 DE 10/10/2026, item 2 ("Pode realizar"): clicar na sub-aba "Fluxo" gravava rascunho
 * sem edição nenhuma. O caminho era `trocarSubSim('flx')` → `renderFld()` → `syncOutputs()` →
 * `persist()`: o simulador da v67 chama `persist()` no fim de TODO redesenho (no
 * `localStorage` isso era de graça), e a ponte tratava cada chamada como edição. Quem só olhou
 * a aba via depois "Recuperamos o que você tinha digitado e ainda não publicou.".
 *
 * POR QUE NA PONTE, E NÃO NA SUB-ABA
 * São ~40 chamadores de `persist()` e vários são DESENHO, não edição (abrir/fechar painel,
 * trocar sub-aba, recalcular). Calar um por um é a correção que esquece o próximo. Aqui a
 * pergunta é a única que importa: o conteúdo que iria para o rascunho é DIFERENTE do que já
 * está registrado? Se é igual, gravar de novo não guarda trabalho nenhum — só cria a linha
 * que dispara o "Recuperamos…". Se mudou, grava, venha de onde vier — inclusive de quem
 * precisa persistir de verdade (concluirFluxo, colagem, importação): nenhuma edição real
 * pode ser engolida, porque a comparação é sobre o PRÓPRIO conteúdo que seria gravado.
 *
 * A referência é o que está na tela depois de uma operação que NÃO é edição minha (a
 * montagem, a versão de outra pessoa chegando pela sonda) e, depois, o último conteúdo
 * aceito pelo rascunho (ou o que foi publicado).
 *
 * `null` = não deu para comparar (estrutura circular, valor que o JSON recusa): aí a regra é
 * a antiga — grava. Errar para o lado de gravar custa uma linha a mais; errar para o outro
 * lado perderia trabalho.
 */
export function impressaoDaEntrada(entrada: unknown): string | null {
  try {
    const s = JSON.stringify(entrada);
    return typeof s === 'string' ? s : null;
  } catch {
    return null;
  }
}

/**
 * Revisão do lote 4 (10/10/2026): o MESMO conteúdo, ignorando a ordem das chaves. O rascunho volta do
 * banco (jsonb) com as chaves reordenadas, e o publicado vem na ordem do mapeamento — a impressão
 * acima (sensível à ordem) dá "diferente" para dois iguais. Em qualquer dúvida, `false` (conta como
 * diferente: mantém o comportamento de antes, que nunca descarta trabalho).
 */
export function mesmoConteudo(a: unknown, b: unknown): boolean {
  const ord = (v: unknown): unknown => (Array.isArray(v)
    ? v.map(ord)
    : v !== null && typeof v === 'object'
      ? Object.fromEntries(Object.keys(v).sort().map((k) => [k, ord((v as Record<string, unknown>)[k])]))
      : v);
  try {
    const x = JSON.stringify(ord(a)), y = JSON.stringify(ord(b));
    return typeof x === 'string' && x === y;
  } catch {
    return false;
  }
}

type Obj = Record<string, unknown>;
const ehObj = (v: unknown): v is Obj => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * Revisão do lote 4 (10/10/2026): o id de uma linha criada na TELA (a vaga "vg1") vira UUID no banco, e a
 * tela continua com o token até recarregar — a mesma vaga, dois ids. Para "o rascunho é o publicado?" o id
 * de linha não é conteúdo: cada vaga (e unidade) vira a sua POSIÇÃO, e os vínculos de unidade → vaga seguem
 * a vaga. Ordem diferente continua sendo diferença (o lado seguro: o rascunho fica).
 */
function semIdsDeLinha(entrada: unknown): unknown {
  if (!ehObj(entrada) || !ehObj(entrada['lev'])) return entrada;
  const lev = entrada['lev'];
  const novo: Obj = { ...lev };
  for (const [uk, vk] of [['unidadesDem', 'vagasDem'], ['unidadesDet', 'vagasDet']] as const) {
    const mapa = new Map<unknown, string>();
    const vagas = lev[vk];
    if (Array.isArray(vagas)) {
      novo[vk] = vagas.map((v, k) => {
        if (!ehObj(v) || !('id' in v)) return v;
        mapa.set(v['id'], `#${k}`);
        return { ...v, id: `#${k}` };
      });
    }
    const uns = lev[uk];
    if (Array.isArray(uns)) {
      novo[uk] = uns.map((u, k) => {
        if (!ehObj(u)) return u;
        const o: Obj = { ...u };
        if ('id' in o) o['id'] = `#${k}`;
        if (Array.isArray(o['vagas'])) o['vagas'] = (o['vagas'] as unknown[]).map((x) => mapa.get(x) ?? x);
        return o;
      });
    }
  }
  return { ...entrada, lev: novo };
}

/** O rascunho é o publicado? `mesmoConteudo`, com os ids de linha pela posição (ver `semIdsDeLinha`). */
export function mesmaEntrada(a: unknown, b: unknown): boolean {
  return mesmoConteudo(semIdsDeLinha(a), semIdsDeLinha(b));
}

/** Igual à referência = nada mudou. Sem referência, ou sem impressão, conta como mudança. */
export function semMudanca(impressao: string | null, referencia: string | null): boolean {
  return impressao !== null && referencia !== null && impressao === referencia;
}
